import React, { useState, useRef } from 'react';
import { JobSubmission } from '../types';
import { validateFile } from '../utils/fileValidation';

interface UploadFormProps {
  onSubmit: (data: JobSubmission) => Promise<void>;
  isSubmitting: boolean;
}

const UploadForm: React.FC<UploadFormProps> = ({ onSubmit, isSubmitting }) => {
  const [candidateName, setCandidateName] = useState('');
  const [objective, setObjective] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate candidate name
    if (!candidateName.trim()) {
      newErrors.candidateName = 'Nome do candidato é obrigatório';
    }

    // Validate objective
    if (!objective.trim()) {
      newErrors.objective = 'Objetivo é obrigatório';
    }

    // Validate file
    if (!file) {
      newErrors.file = 'Selecione um arquivo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !file) {
      return;
    }

    try {
      await onSubmit({
        candidateName: candidateName.trim(),
        objective: objective.trim(),
        file,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validation = validateFile(selectedFile);
      if (!validation.isValid) {
        setErrors((prev) => ({ ...prev, file: validation.error || '' }));
        setFile(null);
      } else {
        setFile(selectedFile);
        setErrors((prev) => ({ ...prev, file: '' }));
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const validation = validateFile(droppedFile);
      if (!validation.isValid) {
        setErrors((prev) => ({ ...prev, file: validation.error || '' }));
        setFile(null);
      } else {
        setFile(droppedFile);
        setErrors((prev) => ({ ...prev, file: '' }));
      }
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <form onSubmit={handleSubmit} className="upload-form">
      <div className="form-group">
        <label htmlFor="candidateName">
          Nome do Candidato <span className="required">*</span>
        </label>
        <input
          type="text"
          id="candidateName"
          value={candidateName}
          onChange={(e) => {
            setCandidateName(e.target.value);
            setErrors((prev) => ({ ...prev, candidateName: '' }));
          }}
          placeholder="Digite o nome completo"
          disabled={isSubmitting}
          className={errors.candidateName ? 'error' : ''}
        />
        {errors.candidateName && (
          <span className="error-message">{errors.candidateName}</span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="objective">
          Objetivo <span className="required">*</span>
        </label>
        <input
          type="text"
          id="objective"
          value={objective}
          onChange={(e) => {
            setObjective(e.target.value);
            setErrors((prev) => ({ ...prev, objective: '' }));
          }}
          placeholder="Ex: Desenvolvedor Full Stack"
          disabled={isSubmitting}
          className={errors.objective ? 'error' : ''}
        />
        {errors.objective && (
          <span className="error-message">{errors.objective}</span>
        )}
      </div>

      <div className="form-group">
        <label>
          Currículo <span className="required">*</span>
        </label>
        <div
          className={`file-upload ${isDragging ? 'dragging' : ''} ${
            errors.file ? 'error' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={isSubmitting}
          />
          {file ? (
            <div className="file-selected">
              <svg
                className="file-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                <polyline points="13 2 13 9 20 9" />
              </svg>
              <div className="file-info">
                <p className="file-name">{file.name}</p>
                <p className="file-size">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              {!isSubmitting && (
                <button
                  type="button"
                  className="remove-file"
                  onClick={() => setFile(null)}
                >
                  ✕
                </button>
              )}
            </div>
          ) : (
            <div className="file-placeholder">
              <svg
                className="upload-icon"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className="upload-text">
                Arraste e solte o arquivo aqui ou{' '}
                <button
                  type="button"
                  className="browse-button"
                  onClick={handleBrowseClick}
                  disabled={isSubmitting}
                >
                  procure no computador
                </button>
              </p>
              <p className="upload-hint">Formatos aceitos: PDF, DOC, DOCX</p>
            </div>
          )}
        </div>
        {errors.file && <span className="error-message">{errors.file}</span>}
      </div>

      <button
        type="submit"
        className="submit-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Processando...' : 'Gerar PDF no padrão Innvo'}
      </button>
    </form>
  );
};

export default UploadForm;
